import { ArrowUpRight, MapPinned, Smartphone, ShieldCheck, Clock, IndianRupee, Phone } from "lucide-react";
import { t, tf } from "@/lib/i18n";
import { useSite } from "@/context/SiteContext";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";

const BENEFITS = [
  { icon: MapPinned, title: "Pickups near you", body: "Get requests from homes, shops and offices in your area, so you spend less time searching." },
  { icon: Clock, title: "Work on your schedule", body: "Accept the pickups that suit your route and timings. You stay in control." },
  { icon: IndianRupee, title: "Fair, open pricing", body: "Customers see indicative rates beforehand, so conversations at the door are quicker and easier." },
  { icon: ShieldCheck, title: "A trusted profile", body: "Simple ID verification gives customers confidence to open their door to you." },
];
const JOIN = [
  { title: "Open the partner app", body: "Visit app.kindsaver.com/partner on any smartphone. No download needed." },
  { title: "Register with your mobile", body: "Enter your name and phone number and verify with an OTP." },
  { title: "Complete verification", body: "Share basic ID details so customers know who is coming." },
  { title: "Start receiving pickups", body: "Once approved, nearby pickup requests start showing up on your phone." },
];

export default function Kabadiwalas() {
  const { content: c } = useSite();
  return (
    <div data-testid="kabadiwalas-page">
      <PageHeader testid="kabadiwalas-header" eyebrow="For kabadiwalas" title={c.partner_title} subtitle={c.partner_subtitle}>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a href={c.partner_app_url} className="btn-leaf" data-testid="kabadiwala-join-btn">{t("Join as a partner")} <ArrowUpRight size={18} /></a>
          <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="btn-ghost" data-testid="kabadiwala-call-btn"><Phone size={16} /> {t("Call us to join")}</a>
        </div>
      </PageHeader>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-5">
          <img src={c.image_kabadiwala} alt={t("A kabadiwala partner with his cart in Jaipur")} className="arch w-full aspect-square object-cover shadow-[0_40px_80px_-40px_rgba(20,40,26,.5)]" />
        </Reveal>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {BENEFITS.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={t(title)} delay={i * 80} className="bg-white rounded-3xl border border-[rgba(20,40,26,.08)] p-6 card-lift" data-testid={`kabadiwala-benefit-${i}`}>
              <span className="w-12 h-12 rounded-2xl bg-[var(--leaf-soft)] text-[var(--leaf-dark)] flex items-center justify-center"><Icon size={22} /></span>
              <h3 className="mt-5 text-lg font-bold">{t(title)}</h3>
              <p className="mt-2 text-sm text-[var(--ink-soft)] leading-relaxed">{t(body)}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Reveal className="rounded-[36px] bg-[var(--ink)] text-white p-8 sm:p-14 grain">
          <div className="flex items-center gap-3 text-[var(--marigold)]"><Smartphone size={20} /><span className="text-xs font-bold tracking-[0.2em] uppercase">{t("How to join")}</span></div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold">{t("Ready in four simple steps.")}</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {JOIN.map((s, i) => (
              <div key={t(s.title)} data-testid={`kabadiwala-step-${i + 1}`}>
                <div className="font-display text-5xl font-extrabold text-[var(--marigold)]">0{i + 1}</div>
                <h3 className="mt-3 text-lg font-bold">{t(s.title)}</h3>
                <p className="mt-2 text-sm text-[#D9D3C3] leading-relaxed">{t(s.body)}</p>
              </div>
            ))}
          </div>
          <a href={c.partner_app_url} className="btn-marigold mt-10" data-testid="kabadiwala-join-btn-bottom">{t("Open partner app")} <ArrowUpRight size={18} /></a>
          <p className="mt-4 text-xs text-[#BDB7A7]">{tf("Need help registering? Call or WhatsApp {phone} — we're happy to help in Hindi.", { phone: c.phone })}</p>
        </Reveal>
      </section>
    </div>
  );
}
